import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  requestDumpStatus,
  dumpDatabaseRequest,
} from "../../../../../../store/actions/settingsActions";

import { addNotification } from "../../../../../../store/actions/notifierActions";
import axiosApi from "../../../../../../utils/axios";
import Warning from "../../../../../../Components/common/Warning/Warning";
import { formatSize, runningFor } from "./restoreUpload";

const BUSY_POLL_INTERVAL = 5000;
const IDLE_POLL_INTERVAL = 15000;

const Dump = () => {
  const dispatch = useDispatch();
  const restoreStatus = useSelector((state) => state.settings.restoreStatus);
  const dumpStatus = useSelector((state) => state.settings.dumpStatus);
  const dumpDate = useSelector((state) => state.settings.dumpDate);
  const dumpInfo = useSelector((state) => state.settings.dumpInfo);
  const [show, setShow] = useState(false);
  const [preparingDownload, setPreparingDownload] = useState(false);

  const isDumping = dumpStatus === "dumping";

  useEffect(() => {
    const fetchData = () => dispatch(requestDumpStatus());

    fetchData();

    const interval = setInterval(fetchData, isDumping ? BUSY_POLL_INTERVAL : IDLE_POLL_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  }, [dispatch, isDumping]);

  const dumpButtonClickHandler = () => {
    setShow(true);
  };

  const dumpDBHandler = () => {
    dispatch(dumpDatabaseRequest());
    setShow(false);
  };

  const downloadDumpHandler = async () => {
    setPreparingDownload(true);
    try {
      const { data } = await axiosApi.post("/api/settings/dump/download-token");
      // The browser's download manager streams the file to disk and can resume it,
      // which a blob download of a multi-GB backup cannot do
      const link = document.createElement("a");
      link.href = `/api/settings/dump/download?token=${encodeURIComponent(data.token)}`;
      link.setAttribute("download", "backup.gz");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      dispatch(
        addNotification({
          message: err?.response?.data?.message ?? "Download failed",
          options: { variant: "error" },
        }),
      );
    } finally {
      setPreparingDownload(false);
    }
  };

  const lastDumpInfo = () => {
    if (isDumping) {
      const elapsed = runningFor(dumpInfo.startedAt);
      return (
        <p className="dump__body__info">
          Backup in progress. This can take a while{elapsed && ` (${elapsed})`}.
        </p>
      );
    } else if (dumpStatus === "success")
      return (
        <p className="dump__body__info dump__body__info--success">
          Last backup operation on {dumpDate} was successfull
          {dumpInfo.fileSize ? ` (${formatSize(dumpInfo.fileSize)})` : ""}.{" "}
          {dumpInfo.fileAvailable
            ? 'Click on "Download" button to download that backup file.'
            : 'The backup file is no longer available. Click on "Backup" button to backup database.'}
        </p>
      );
    else if (dumpStatus === "error")
      return (
        <p className="dump__body__info dump__body__info--error">
          Last backup operation on {dumpDate} was failed. {dumpInfo.message} Click on "Backup"
          button to backup database.
        </p>
      );
    else return "";
  };

  return (
    <div className="dump">
      <h1 className="dump__header">Backup</h1>
      <div className="dump__body">
        <div className="dump__body__header">
          <h3>Backup</h3>
          <p>
            NOTE: Click on "Backup" button to backup database. Only report related data is included
            in backup. Artifacts, files and integration configs are not included in backup. Actions
            during dump process will not be included in dump operation. Backuping database can take
            a while. Click on "Download" button to download backup file.
          </p>
        </div>
        <div className="dump__body__content">
          {restoreStatus !== "restoring" && lastDumpInfo()}
          <div className="dump__body__actions">
            <button
              className="primary-button"
              onClick={dumpButtonClickHandler}
              disabled={isDumping || restoreStatus === "restoring"}>
              {restoreStatus === "restoring"
                ? "Restoring..."
                : isDumping
                  ? "Backuping..."
                  : "Backup"}
            </button>
            <button
              className="primary-button"
              onClick={downloadDumpHandler}
              disabled={
                dumpStatus !== "success" ||
                !dumpInfo.fileAvailable ||
                restoreStatus === "restoring" ||
                preparingDownload
              }>
              Download
            </button>
          </div>
        </div>
      </div>
      <Warning
        onClickOk={dumpDBHandler}
        show={show}
        setShow={setShow}
        text={
          "If you click yes, you will lose your submissions with the running and reporting status. Do you still want to continue ?"
        }
        article="Warning"
        cancelDisable={false}
      />
    </div>
  );
};

export default Dump;
